/** Connection metadata registration (allowlisted fields only; no provider bodies or secrets). */
import { z } from 'zod';
import { AccountIdSchema, ConnectionIdSchema, DeviceIdSchema, ProviderSchema, WorkspaceIdSchema } from '../ids.js';
import { ConnectionHealthSchema, SyncJobStateSchema } from '../status.js';
import { EmptyRequestSchema, IsoDateTimeSchema, NoQuerySchema, defineRoute } from './common.js';
import { CapabilityManifestSchema, ConnectionSchema } from './entities.js';

export const ConnectionRegisterRequestSchema = z
  .object({
    deviceId: DeviceIdSchema,
    provider: ProviderSchema,
    fingerprint: z.string().min(16).max(128),
    workspaceId: WorkspaceIdSchema.nullable().default(null),
    tenantLabel: z.string().max(120).nullable().default(null),
    capabilityManifest: CapabilityManifestSchema,
    health: ConnectionHealthSchema,
    /** Local connection id so the registry can map local copies to registry accounts. */
    localConnectionId: ConnectionIdSchema,
    /** Set when the connection replaces credentials of an existing registration. */
    replacesConnectionId: ConnectionIdSchema.optional(),
  })
  .strict();
export type ConnectionRegisterRequest = z.infer<typeof ConnectionRegisterRequestSchema>;

export const ConnectionReportRequestSchema = z
  .object({
    health: ConnectionHealthSchema.optional(),
    lastSyncAt: IsoDateTimeSchema.optional(),
    lastSyncState: SyncJobStateSchema.optional(),
    capabilityManifest: CapabilityManifestSchema.optional(),
    workspaceId: WorkspaceIdSchema.nullable().optional(),
  })
  .strict();
export type ConnectionReportRequest = z.infer<typeof ConnectionReportRequestSchema>;

export const ConnectionResponseSchema = z.object({ connection: ConnectionSchema });
export const ConnectionListResponseSchema = z.object({ items: z.array(ConnectionSchema) });

export const ConnectionParamsSchema = z.object({ accountId: AccountIdSchema, connectionId: ConnectionIdSchema });

export const connectionRoutes = {
  'connections.register': defineRoute({
    id: 'connections.register',
    method: 'POST',
    path: '/v1/accounts/:accountId/connections',
    auth: 'desktop',
    capability: 'account.connect',
    paramsSchema: z.object({ accountId: AccountIdSchema }),
    requestSchema: ConnectionRegisterRequestSchema,
    responseSchema: ConnectionResponseSchema,
    description: 'Register connection metadata for an assigned account. Idempotent on (account, device, fingerprint).',
  }),
  'connections.list': defineRoute({
    id: 'connections.list',
    method: 'GET',
    path: '/v1/accounts/:accountId/connections',
    auth: 'desktop',
    paramsSchema: z.object({ accountId: AccountIdSchema }),
    requestSchema: NoQuerySchema,
    responseSchema: ConnectionListResponseSchema,
    description: 'Connections registered for an assigned account across devices (metadata only).',
  }),
  'connections.report': defineRoute({
    id: 'connections.report',
    method: 'PATCH',
    path: '/v1/accounts/:accountId/connections/:connectionId',
    auth: 'desktop',
    capability: 'account.connect',
    paramsSchema: ConnectionParamsSchema,
    requestSchema: ConnectionReportRequestSchema,
    responseSchema: ConnectionResponseSchema,
    description: 'Report health / last sync for a connection this device owns.',
  }),
  'connections.remove': defineRoute({
    id: 'connections.remove',
    method: 'DELETE',
    path: '/v1/accounts/:accountId/connections/:connectionId',
    auth: 'desktop',
    capability: 'account.connect',
    paramsSchema: ConnectionParamsSchema,
    requestSchema: EmptyRequestSchema,
    responseSchema: ConnectionResponseSchema,
    description: 'Mark a connection removed on this device.',
  }),
} as const;
